import { events, pageViewTypes } from "@constants/tracking";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import PrintButton from "@features/report/components/print/PrintButton";
import PrintableReportContainer from "@features/report/components/print/PrintableReportContainer";
import { usePrinting } from "@features/report/hooks/usePrinting";
import { useReport } from "@features/report/hooks/useReport";
import { useReportAnalytics } from "@features/report/hooks/useReportAnalytics";
import { useSessionItems } from "@features/session/hooks/useSessionItems";
import useSessionStore from "@features/session/hooks/useSessionStore";
import useAnalytics from "@hooks/useAnalytics";
import {
	type ItemsFilter,
	filterReviewItems,
	hasMarkedReviewItems,
	isReportSummativeTest,
	resolveItemsFilter,
} from "@memorang/helpers/report";
import { isSkillTag, sortTagsBySortOrder } from "@memorang/helpers/tag";
import Box from "@memorang/ui/components/Box";
import { ConnectedButtonGroup } from "@memorang/ui/components/ConnectedButtonGroup";
import EmptyState from "@memorang/ui/components/EmptyView";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { SessionReportAnswerRow } from "@memorang/ui/components/SessionReport/SessionReportAnswerRow";
import { SessionReportCard } from "@memorang/ui/components/SessionReport/SessionReportCard";
import { SessionReportCardSkeleton } from "@memorang/ui/components/SessionReport/SessionReportCardSkeleton";
import { useDevice } from "@memorang/ui/useDevice";
import DocumentTitle from "components/DocumentTitle";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";

const isItemsFilter = (value: string): value is ItemsFilter => {
	return (
		value === "all" ||
		value === "correct" ||
		value === "incorrect" ||
		value === "marked"
	);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
const SessionReportItemsRoute = () => {
	useAnalytics(
		useMemo(
			() => ({
				type: pageViewTypes.reportItems,
			}),
			[],
		),
	);

	const { t } = useTranslation();
	const router = useRouter();
	const { id: sessionId } = useLocalSearchParams<{ id: string }>();
	const { isMobile } = useDevice();
	const { isPrintMode } = usePrinting();

	const sessionStoreIsSummativeTest = useSessionStore(
		(state) => state.isSummativeTest,
	);
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const { reportDetails, isReportError, isReportPending } = useReport();
	const { trackReportEvent } = useReportAnalytics({
		reportDetails,
		view: pageViewTypes.reportItems,
	});

	const isSummativeTest = reportDetails
		? isReportSummativeTest(reportDetails, { studyPackCollectionId })
		: Boolean(sessionStoreIsSummativeTest);

	const { data: sessionItems, isPending: sessionItemsLoading } =
		useSessionItems({
			sessionId: reportDetails?.id ?? 0,
			isSummativeTest,
			enabled: Boolean(reportDetails?.id),
		});

	const skillTags = useMemo(
		() =>
			sortTagsBySortOrder(
				(reportDetails?.tagDetails ?? []).filter((tag) => isSkillTag(tag)),
			),
		[reportDetails?.tagDetails],
	);

	const hasMarkedItems = hasMarkedReviewItems(sessionItems);
	const [_itemsFilter, setItemsFilter] = useState<ItemsFilter>("all");
	const itemsFilter = resolveItemsFilter(_itemsFilter, hasMarkedItems);

	useEffect(() => {
		if (_itemsFilter === "marked" && !hasMarkedItems) {
			setItemsFilter("all");
		}
	}, [_itemsFilter, hasMarkedItems]);

	const filteredItems = useMemo(
		() => filterReviewItems(sessionItems, itemsFilter),
		[sessionItems, itemsFilter],
	);

	const filterButtons = useMemo(() => {
		const style = {
			flexGrow: 1,
			flexShrink: 1,
			flexBasis: "auto" as const,
		};
		const buttons = [
			{
				value: "all",
				label: t("report.itemDetail.filterAll"),
				style,
			},
			{
				value: "correct",
				label: t("report.itemDetail.filterCorrect"),
				style,
			},
			{
				value: "incorrect",
				label: t("report.itemDetail.filterIncorrect"),
				style,
			},
		];
		if (hasMarkedItems) {
			buttons.push({
				value: "marked",
				label: t("report.itemDetail.filterMarked"),
				style,
			});
		}
		return buttons;
	}, [hasMarkedItems, t]);

	const filterLabel = useMemo(() => {
		switch (itemsFilter) {
			case "correct":
				return t("report.itemDetail.filterCorrect");
			case "incorrect":
				return t("report.itemDetail.filterIncorrect");
			case "marked":
				return t("report.itemDetail.filterMarked");
			default:
				return t("report.itemDetail.filterAll");
		}
	}, [itemsFilter, t]);

	if (isReportError || !(isReportPending || reportDetails)) {
		return (
			<Redirect
				href={{
					pathname: "/session/[id]/report" as const,
					params: { id: sessionId },
				}}
			/>
		);
	}

	if (isPrintMode) {
		return <PrintableReportContainer />;
	}

	const handleNavigateBack = () => {
		if (router.canGoBack()) {
			router.back();
			return;
		}
		router.push({
			pathname: "/session/[id]/report",
			params: { id: reportDetails?.id ?? 0 },
		});
	};

	const handleNavigateToReport = () => {
		if (!reportDetails) {
			return;
		}
		router.push({
			pathname: "/session/[id]/report",
			params: { id: reportDetails.id },
		});
	};

	const breadcrumbs = reportDetails
		? [
				{
					label: `${reportDetails.examName} - ${reportDetails.id}`,
					action: handleNavigateToReport,
				},
				{
					label: t("report.itemDetail.title"),
				},
			]
		: undefined;

	const isLoading =
		isReportPending || !reportDetails ? true : sessionItemsLoading;
	const showEmptyState = !isLoading && filteredItems.length === 0;
	const isMarkable = Boolean(isSummativeTest && hasMarkedItems);
	const hideSkills = skillTags.length <= 1;

	const handleItemsFilterChange = (value: string) => {
		if (isItemsFilter(value)) {
			trackReportEvent(events.sessionReportFiltered, {
				filterType: "performance",
				value,
			});
			setItemsFilter(value);
		}
	};

	return (
		<>
			<DocumentTitle title={t("report.itemDetail.title")} />
			<AppBar
				title={t("report.itemDetail.title")}
				breadcrumbs={breadcrumbs}
				onPressBack={handleNavigateBack}
				hideCommandBar
				hideExamSwitcher
				renderRight={() => <PrintButton />}
			/>
			<Box flex={1}>
				<ScrollContainer maxWidth="sm">
					<Box
						paddingTop={16}
						paddingBottom={isMobile ? 24 : 64}
						paddingHorizontal={isMobile ? 0 : 32}
						gap={16}
					>
						<Box
							flexDirection={isMobile ? "column" : "row"}
							alignItems={isMobile ? "stretch" : "center"}
							justifyContent="space-between"
							gap={16}
							paddingHorizontal={isMobile ? 16 : 0}
						>
							{isMobile ? null : (
								<Text variant="headlineSmall">
									{t("report.itemDetail.title")}
								</Text>
							)}
							<ConnectedButtonGroup
								value={itemsFilter}
								onValueChange={handleItemsFilterChange}
								buttons={filterButtons}
							/>
						</Box>

						{isLoading ? (
							<SessionReportCardSkeleton />
						) : showEmptyState ? (
							<Box
								justifyContent="center"
								alignItems="center"
								paddingVertical={48}
								paddingHorizontal={16}
							>
								<EmptyState
									icon="filter-remove-outline"
									title={t("report.itemDetail.emptyTitle")}
									subtitle={t("report.itemDetail.emptyDescription", {
										filter: filterLabel,
									})}
									cta={t("report.itemDetail.emptyCta")}
									handleAction={() => setItemsFilter("all")}
								/>
							</Box>
						) : (
							<SessionReportCard.Frame>
								<SessionReportCard.Body paddingHorizontal={16}>
									{filteredItems.map((sessionItem, index) => (
										<SessionReportAnswerRow
											key={sessionItem.id}
											item={sessionItem}
											isMarkable={isMarkable}
											hideSkills={hideSkills}
											hideDivider={index === filteredItems.length - 1}
										/>
									))}
								</SessionReportCard.Body>
							</SessionReportCard.Frame>
						)}
					</Box>
				</ScrollContainer>
			</Box>
		</>
	);
};

export default SessionReportItemsRoute;
